import { RootState } from '@/store';
import { FileExcelOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Col, DatePicker, Input, Row, Select, Spin, Table, Tag, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updatePropsReport } from '../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import CompanyService from '../../../../services/company/CompanyService';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import UserService from '../../../../services/user/UserService';
import { saveBlobCrossPlatform } from '../../../../utils/saveBlobCrossPlatform';
import { PERIODICITY_OPTIONS } from '../ManageMyMentenancePlanning/maintenancePlanningViewUtils';
import MentenancePlanningReportExportModal from './MentenancePlanningReportExportModal';
import './MentenancePlanningReportTab.css';

const { Option } = Select;

const PERIODICITY_SELECT_OPTIONS = PERIODICITY_OPTIONS.map((periodicity) => ({ label: periodicity, value: periodicity }));

const normalizeName = (value?: string | null) => (value ?? '').trim().toLowerCase();

const getCompanyName = (record: any): string => (record?.metadata?.company?.name ?? record?.company?.name ?? '').trim();

const getWorkPlaceName = (record: any): string =>
  (record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? '').trim();

const getFilenameFromResponse = (response: Response) => {
  const contentDisposition = response.headers.get('Content-Disposition');
  const matches = contentDisposition ? /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition) : null;
  return matches?.[1] ? decodeURIComponent(matches[1].replace(/['"]/g, '')) : null;
};

const hasInterventionFile = (record: any) =>
  !!record?.interventionFile?.id && record.interventionFile.isActive !== false;

const sanitizeFilename = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildPdfFilename = (record: any) => {
  const base = [getCompanyName(record), getWorkPlaceName(record), record?.interventionFile?.number]
    .filter(Boolean)
    .join(' ');
  return `${sanitizeFilename(base || record?.interventionFile?.id || 'file')}.pdf`;
};

const MentenancePlanningReportTab: React.FC = () => {
  const dispatch = useDispatch();
  const { message } = App.useApp();

  const searchTerm = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsReport?.searchTerm);

  const selectedMonthString = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsReport?.selectedMonth
  );

  // Convert string to dayjs object for component use (DatePicker works better with dayjs)
  const selectedMonth = useMemo(() => {
    if (!selectedMonthString) return null;
    try {
      // Parse the ISO string and create a dayjs object
      const dayjsDate = dayjs(selectedMonthString);
      // Ensure it's valid and set to the start of the month
      return dayjsDate.isValid() ? dayjsDate.startOf('month') : null;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  }, [selectedMonthString]);

  const selectedTechnician = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsReport?.selectedTechnician
  );
  const selectedStatusId = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsReport?.selectedStatus
  );

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState<string | undefined>(undefined);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [pagination, setPagination] = useState<any>({ current: 1, pageSize: 10 });

  const reportMonth = selectedMonth ? selectedMonth.month() + 1 : moment().month() + 1;
  const reportYear = selectedMonth ? selectedMonth.year() : moment().year();
  const reportMonthLabel = String(reportMonth).padStart(2, '0');

  const {
    data: mentenancePlannings,
    isFetching: isMentenancePlanningLoading,
    refetch: refetch,
  } = useQuery({
    queryKey: [
      'mentenancePlanningsReport',
      selectedMonthString || moment().format('YYYY-MM'),
      searchTerm,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
    ],
    queryFn: () =>
      MentenancePlanningService.searchAll({
        isAssignedTechnicianIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isInterventionFileIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isStatusIncluded: true,
        isWorkPlaceIncluded: true,
        month: reportMonth,
        year: reportYear,
        termen: searchTerm,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician , senior_tehnician, external_tehnician',
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: printInterventionFile } = useMutation<
    Response,
    Error,
    { record: any; action: 'download' | 'newTab'; targetWindow?: Window | null }
  >({
    mutationFn: async ({ record }) => {
      const response = await InterventionFileService.print(record.interventionFile.id);
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      return response;
    },
    onSuccess: async (response, { record, action, targetWindow }) => {
      const blob = await response.blob();
      const filename = buildPdfFilename(record);

      if (action === 'download') {
        saveBlobCrossPlatform(blob, filename);
        return;
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const escapedFilename = filename.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapedFilename}</title><style>body{margin:0;padding:0;overflow:hidden}.pdf{position:fixed;top:0;left:0;right:0;bottom:0;width:100%;height:100vh;border:none}.dl{position:fixed;top:5px;right:85px;z-index:9999;width:40px;height:40px;padding:0;border:none;border-radius:50%;background:rgba(50,50,50,0.85);cursor:pointer;display:flex;align-items:center;justify-content:center}.dl:hover{background:rgba(70,70,70,0.9)}.dl svg{color:#fff;pointer-events:none}</style></head><body><iframe class="pdf" src="${blobUrl}" title="PDF"></iframe><a class="dl" href="${blobUrl}" download="${escapedFilename}" title="Descarcă PDF"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></a></body></html>`;
      const htmlBlob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const htmlBlobUrl = window.URL.createObjectURL(htmlBlob);
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = htmlBlobUrl;
      } else {
        window.open(htmlBlobUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => window.URL.revokeObjectURL(htmlBlobUrl), 60_000);
    },
    onError: (error, { targetWindow }) => {
      console.error('Error processing your request:', error);
      targetWindow?.close();
      message.error('Eroare la încărcarea fișierului PDF.');
    },
  });

  const openInterventionFilePdf = (record: any) => {
    // In APK mode, treat open in new tab as download since APK can't handle browser tabs
    const w = window as any;
    const isApkMode = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function';
    if (isApkMode) {
      printInterventionFile({ record, action: 'download' });
      return;
    }

    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      message.warning('Permiteți ferestrele pop-up pentru a deschide PDF-ul într-un tab nou.');
      return;
    }
    printInterventionFile({ record, action: 'newTab', targetWindow });
    try {
      targetWindow.opener = null;
    } catch {}
  };

  const { mutate: exportReport, isPending: isExportingReport } = useMutation<Response, Error, string[]>({
    mutationFn: (columns) => MentenancePlanningService.exportReport({ month: reportMonth, year: reportYear, columns }),
    onSuccess: async (response) => {
      // Validation errors come back as a JSON body with status 200.
      if (response.headers.get('Content-Type')?.includes('application/json')) {
        const data = await response.json();
        message.error(data?.errors?.[0] ?? 'Eroare la generarea raportului Excel.');
        return;
      }

      const blob = await response.blob();
      const fallbackFilename = `raport_mentenante_${reportMonthLabel}_${reportYear}.xlsx`;
      saveBlobCrossPlatform(blob, getFilenameFromResponse(response) ?? fallbackFilename);
      setIsExportModalOpen(false);
    },
    onError: (error) => {
      console.error('Error exporting maintenance report:', error);
      message.error('Eroare la generarea raportului Excel.');
    },
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (searchTerm === '') {
      refetch();
    }
  }, [searchTerm, refetch]);

  const handleMonthChange = useCallback(
    (date: Dayjs | null) => {
      try {
        // Store as ISO string instead of moment object to avoid serialization issues
        // Use startOf('month') to ensure we only store the month and year, not the specific day/time
        let dateString = null;
        if (date && date.isValid()) {
          // Create a new dayjs object with just year and month to avoid timezone issues
          const year = date.year();
          const month = date.month();
          const cleanDate = dayjs().year(year).month(month).startOf('month');
          // Convert to moment for consistent API calls
          const momentDate = moment(cleanDate.toDate());
          dateString = momentDate.toISOString();
        }

        dispatch(updatePropsReport({ field: 'selectedMonth', value: dateString }));
        dispatch(updatePropsReport({ field: 'selectedStatus', value: null }));
        dispatch(updatePropsReport({ field: 'selectedTechnician', value: null }));
      } catch (error) {
        console.error('Error handling month change:', error);
        // If there's an error, clear the date
        dispatch(updatePropsReport({ field: 'selectedMonth', value: null }));
      }
    },
    [dispatch]
  );

  const handleStatusClick = useCallback(
    (statusId: number | null) => {
      dispatch(updatePropsReport({ field: 'selectedStatus', value: statusId }));
    },
    [dispatch]
  );

  const handleTechnicianChange = useCallback(
    (value: string | null) => {
      dispatch(
        updatePropsReport({
          field: 'selectedTechnician',
          value: value ? _users?.find((u: any) => u.id == value) : null,
        })
      );
    },
    [_users, dispatch]
  );

  const resetToFirstPage = () => setPagination((current: any) => ({ ...current, current: 1 }));

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handlePeriodicityChange = (value?: string) => {
    setSelectedPeriodicity(value);
    resetToFirstPage();
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      current: page,
      pageSize: pageSize,
    });
  };

  const handleSearch = useCallback((value: string) => {
    debouncedSearch(value);
  }, []);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updatePropsReport({ field: 'searchTerm', value }));
    }, 500),
    [dispatch]
  );

  if (isMentenancePlanningLoading || isUsersLoading || isCompaniesLoading || isSystemTypesLoading) {
    return <Spin className='spinner' size='large' />;
  }

  const matchesTechnician = (item: any) =>
    !selectedTechnician || item?.assignedTechnician?.id === selectedTechnician?.id;
  const matchesPeriodicity = (item: any) =>
    !selectedPeriodicity || normalizeName(item?.periodicity) === normalizeName(selectedPeriodicity);

  const filteredPlannings = (mentenancePlannings ?? []).filter(
    (item: any) => matchesTechnician(item) && matchesPeriodicity(item)
  );

  const summary = {
    total: filteredPlannings?.length ?? 0,
    assigned: filteredPlannings?.filter((d: any) => d.statusId === 2)?.length ?? 0,
    inProgress: filteredPlannings?.filter((d: any) => d.statusId === 3)?.length ?? 0,
    finalized: filteredPlannings?.filter((d: any) => d.statusId === 4)?.length ?? 0,
    reassigned: filteredPlannings?.filter((d: any) => d.statusId === 5)?.length ?? 0,
    unassigned: filteredPlannings?.filter((d: any) => d.statusId === 1)?.length ?? 0,
  };

  const filteredData = filteredPlannings.filter(
    (item: any) => selectedStatusId === null || item?.statusId === selectedStatusId
  );

  const columns = [
    { title: 'Numar', dataIndex: 'number', key: 'number' },
    { title: 'Inregistrata', dataIndex: ['registeredByUser', 'name'], key: 'registeredByUser' },
    {
      title: 'Companie',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct de lucru',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
      key: 'workPlace',
    },
    { title: 'Tehnician', dataIndex: ['assignedTechnician', 'name'], key: 'assignedTechnician' },
    {
      title: 'Sisteme',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>{mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')}</>
      ),
    },
    {
      title: 'Periodicitate',
      dataIndex: 'periodicity',
      key: 'periodicity',
      render: (periodicity: string) => periodicity || '-',
    },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
    },
    { title: 'Status', dataIndex: ['status', 'name'], key: 'status' },
    {
      title: 'Data programata',
      dataIndex: 'appointment',
      key: 'appointment',
      render: (text: string) => (text ? moment(text).format('DD-MM-YYYY') : 'N/A'),
    },
    {
      title: 'Termen limita',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      render: (text: string) => (text ? moment(text).format('DD-MM-YYYY') : 'N/A'),
    },
    {
      title: 'Fisa de interventie',
      key: 'interventionFile',
      render: (record: any) =>
        hasInterventionFile(record) ? (
          <Tag color={'blue'} style={{ cursor: 'pointer' }} onClick={() => openInterventionFilePdf(record)}>
            {record.interventionFile.number}
          </Tag>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className='table-page-no-h-scroll'>
      <h3 className='maintenance-report-title'>Report Mentenante</h3>
      <Row justify='end' style={{ marginBottom: '16px' }}>
        <Tooltip title='Include toate mentenantele din luna selectata, indiferent de filtrele aplicate'>
          <Button icon={<FileExcelOutlined />} loading={isExportingReport} onClick={() => setIsExportModalOpen(true)}>
            Descarca raport Excel
          </Button>
        </Tooltip>
      </Row>
      <MentenancePlanningReportExportModal
        open={isExportModalOpen}
        loading={isExportingReport}
        periodLabel={`${reportMonthLabel}.${reportYear}`}
        onCancel={() => setIsExportModalOpen(false)}
        onConfirm={(columns) => exportReport(columns)}
      />
      <Row gutter={16} style={{ marginBottom: '16px', rowGap: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Input.Search
            placeholder='Cauta...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={3}>
          <DatePicker
            key={`month-picker-${selectedMonthString || 'default'}`}
            value={selectedMonth}
            picker='month'
            placeholder='Filtreaza dupa luna'
            onChange={handleMonthChange}
            allowClear
            className='month-picker'
            format='MM-YYYY'
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces?.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={3}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes?.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={3}>
          <Select
            value={selectedTechnician?.id}
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder='Filtreaza dupa Tehnician'
            onChange={handleTechnicianChange}
            optionFilterProp='children'
          >
            {_users?.map((user: any) => (
              <Option key={user.id} value={user.id}>
                {user.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={3}>
          <Select
            value={selectedPeriodicity}
            allowClear
            style={{ width: '100%' }}
            placeholder='Filtreaza dupa periodicitate'
            onChange={handlePeriodicityChange}
            options={PERIODICITY_SELECT_OPTIONS}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} justify='center' style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Mentenante</div>
            <Button
              block
              type={selectedStatusId === null ? 'primary' : 'default'}
              onClick={() => handleStatusClick(null)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.total}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Finalizate</div>
            <Button
              block
              type={selectedStatusId === 4 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(4)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.finalized}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Programate</div>
            <Button
              block
              type={selectedStatusId === 2 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(2)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.assigned}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>In Procesare</div>
            <Button
              block
              type={selectedStatusId === 3 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(3)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.inProgress}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Reprogramate</div>
            <Button
              block
              type={selectedStatusId === 5 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(5)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.reassigned}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Neprogramate</div>
            <Button
              block
              type={selectedStatusId === 1 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(1)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.total - summary.finalized - summary.assigned - summary.reassigned - summary?.inProgress}
            </Button>
          </div>
        </Col>
      </Row>

      <div className='table-scroll-wrapper'>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey='number'
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredData?.length ?? 0,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '20', '30', '50'],
            showSizeChanger: true,
          }}
          bordered
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );
};

export default MentenancePlanningReportTab;
